import { z } from 'zod';
import { DEFAULT_COUNTRY_CODE } from '@/constants/countryCodes';
import {
  VALIDATION_CONSTANTS,
  VALIDATION_MESSAGES,
  getAddressStreetLength,
  getTotalAddressLength,
  normalizeIndianPincode,
} from '@/constants/validationConstants';
import { isInvalidAllZerosPhone, isValidPhoneNumber } from '@/utils/phoneNumber.utils';

const getAgeInYears = (dateOfBirth: string): number | null => {
  const [yearPart, monthPart, dayPart] = dateOfBirth.split('-');
  const year = Number(yearPart);
  const month = Number(monthPart);
  const day = Number(dayPart);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() + 1 - month;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
    age--;
  }
  return age;
};

export const dateOfBirthSchema = z
  .string()
  .min(1, 'Date of birth is required')
  .refine((date) => getAgeInYears(date) !== null, {
    message: VALIDATION_MESSAGES.DATE_OF_BIRTH.INVALID_FORMAT,
  })
  .refine(
    (date) => {
      const [yearPart, monthPart, dayPart] = date.split('-');
      const year = Number(yearPart);
      const month = Number(monthPart);
      const day = Number(dayPart);
      const today = new Date();
      const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
      const birthUtc = Date.UTC(year, month - 1, day);
      return birthUtc <= todayUtc;
    },
    {
      message: VALIDATION_MESSAGES.DATE_OF_BIRTH.FUTURE_DATE,
    }
  )
  .refine(
    (date) => {
      const age = getAgeInYears(date);
      return (
        age !== null && age >= VALIDATION_CONSTANTS.AGE.MIN && age <= VALIDATION_CONSTANTS.AGE.MAX
      );
    },
    {
      message: VALIDATION_MESSAGES.DATE_OF_BIRTH.INVALID_AGE,
    }
  );

const buildUserProfileSchema = (dateOfBirth: z.ZodType<string>) =>
  z
    .object({
      role: z.string().min(1, 'Role is required').default('client'),
      client_id: z.string().optional().nullable(),
      title: z.string().optional().nullable(),
      first_name: z
        .string()
        .min(1, 'First name is required')
        .max(50, 'First name must be at most 50 characters')
        .regex(/^[A-Za-z ]*$/, 'First name can only contain letters and spaces'),
      last_name: z
        .string()
        .min(1, 'Last name is required')
        .max(50, 'Last name must be at most 50 characters')
        .regex(/^[A-Za-z ]*$/, 'Last name can only contain letters and spaces'),
      email: z
        .string()
        .min(1, 'Email is required')
        .email('Invalid email address')
        .transform((value) => value.toLowerCase()),
      date_of_birth: dateOfBirth,
      gender: z.enum(['M', 'F', 'O', 'Other'], {
        message: 'Gender is required',
      }),
      occupation: z.string().optional().nullable(),
      relationship_status: z.string().optional().nullable(),
      relationship_status_custom: z.string().optional().nullable(),
      phone_number: z
        .string()
        .min(1, 'Phone number is required')
        .refine((val) => /^\d+$/.test(val), {
          message: 'Phone number must contain only digits',
        })
        .refine((val) => !isInvalidAllZerosPhone(val), {
          message: 'Phone number cannot be all zeros',
        }),
      phone_country_code: z.string().default(DEFAULT_COUNTRY_CODE),
      alternate_phone: z
        .string()
        .optional()
        .nullable()
        .refine((val) => !val || /^\d+$/.test(val), {
          message: 'Phone number must contain only digits',
        })
        .refine((val) => !isInvalidAllZerosPhone(val), {
          message: 'Alternate phone cannot be all zeros',
        }),
      alternate_phone_country_code: z.string().default(DEFAULT_COUNTRY_CODE),
      alternate_email: z
        .string()
        .email('Invalid email address')
        .optional()
        .or(z.literal(''))
        .nullable(),
      preferred_contact_method: z.enum(['email', 'phone', 'sms']).default('email'),
      preferred_languages: z.array(z.string()).min(1, 'At least one language must be selected'),
      house_no: z.string().optional().nullable(),
      street: z.string().optional().nullable(),
      address_line_1: z.string().optional().nullable(),
      address_line_2: z.string().optional().nullable(),
      landmark: z.string().optional().nullable(),
      city: z.string().optional().nullable(),
      state: z.string().optional().nullable(),
      country: z.string().optional().nullable(),
      zipcode: z
        .union([z.string(), z.null(), z.undefined()])
        .transform((v) => {
          if (v == null || v === '') return '';
          return normalizeIndianPincode(String(v));
        })
        .refine((v) => v === '' || /^\d{6}$/.test(v), {
          message: VALIDATION_MESSAGES.ZIPCODE.INVALID,
        }),
      emergency_contact_name: z
        .string()
        .max(50, 'Name must be at most 50 characters')
        .regex(/^[A-Za-z ]*$/, 'Name can only contain letters and spaces')
        .optional()
        .nullable(),
      emergency_contact_relation: z.string().optional().nullable(),
      emergency_contact_number: z
        .string()
        .optional()
        .nullable()
        .refine((val) => !val || /^\d+$/.test(val), {
          message: 'Phone number must contain only digits',
        })
        .refine((val) => !isInvalidAllZerosPhone(val), {
          message: 'Emergency contact phone cannot be all zeros',
        }),
      emergency_contact_number_country_code: z.string().default(DEFAULT_COUNTRY_CODE),
      specialization: z.string().optional().nullable(),
      registration_number: z.string().optional().nullable(),
      registration_state: z.string().optional().nullable(),
      signature: z.any().optional().nullable(),
      signature_file_name: z.string().optional().nullable(),
      organizations: z.array(z.string()).optional().default([]),
      clinics: z.array(z.string()).optional().default([]),
      // Doctor-assignment fields. Choices mirror backend Language enum.
      primary_language: z.string().optional().nullable(),
      secondary_language: z.string().optional().nullable(),
      languages_spoken: z.array(z.string()).optional().default([]),
      is_on_leave_until: z.string().optional().nullable(),
    })
    .superRefine((data, ctx) => {
      const validatePhoneLength = (
        phone: string | null | undefined,
        code: string | null | undefined,
        path: (string | number)[]
      ) => {
        if (!phone) return;

        if (code === '+91') {
          if (phone.length !== 10) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path,
              message: 'Enter exactly 10 digits for India (+91)',
            });
          }
        } else if (phone.length < 4 || phone.length > 14) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path,
            message: 'Enter between 4 and 14 digits for the selected country code',
          });
        }
      };

      if (!isValidPhoneNumber(data.phone_number)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['phone_number'],
          message: 'Enter a valid phone number',
        });
      } else {
        validatePhoneLength(data.phone_number, data.phone_country_code, ['phone_number']);
      }

      validatePhoneLength(data.alternate_phone || undefined, data.alternate_phone_country_code, [
        'alternate_phone',
      ]);

      validatePhoneLength(
        data.emergency_contact_number || undefined,
        data.emergency_contact_number_country_code,
        ['emergency_contact_number']
      );

      if (data.relationship_status === 'Other') {
        if (!data.relationship_status_custom?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['relationship_status_custom'],
            message: 'Please specify your relationship status',
          });
        }
      }

      if (data.role?.toUpperCase() === 'DOCTOR') {
        if (!data.specialization?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['specialization'],
            message: 'Specialization is required for doctors',
          });
        }
        if (!data.registration_number?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['registration_number'],
            message: 'Registration number is required for doctors',
          });
        }
      }

      const isClient = data.role?.toLowerCase() === 'client';
      const hasAnyAddressField = Boolean(
        data.address_line_1?.trim() ||
        data.city?.trim() ||
        data.state?.trim() ||
        data.country?.trim() ||
        data.zipcode?.trim()
      );

      const streetAddressLength = getAddressStreetLength({
        house_no: data.house_no,
        street: data.street,
        address_line_1: data.address_line_1,
        address_line_2: data.address_line_2,
        landmark: data.landmark,
      });

      const totalAddressLength = getTotalAddressLength({
        house_no: data.house_no,
        street: data.street,
        address_line_1: data.address_line_1,
        address_line_2: data.address_line_2,
        landmark: data.landmark,
        city: data.city,
        state: data.state,
        country: data.country,
        zipcode: data.zipcode,
      });

      if (isClient && !hasAnyAddressField) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['address_line_1'],
          message: VALIDATION_MESSAGES.ADDRESS.REQUIRED,
        });
        return;
      }

      if (isClient || hasAnyAddressField) {
        if (streetAddressLength < VALIDATION_CONSTANTS.ADDRESS.MIN_ALPHANUMERIC) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['address_line_1'],
            message: VALIDATION_MESSAGES.ADDRESS.STREET_MIN_LENGTH,
          });
        }
      }
      if (totalAddressLength > VALIDATION_CONSTANTS.ADDRESS.TOTAL_MAX_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['address_line_1'],
          message: VALIDATION_MESSAGES.ADDRESS.TOTAL_MAX_LENGTH,
        });
      }
    });

export const userProfileSchema = buildUserProfileSchema(dateOfBirthSchema);

export const savedDateOfBirthProfileSchema = buildUserProfileSchema(
  z.string().min(1, 'Date of birth is required')
);

export type UserProfileFormData = z.infer<typeof userProfileSchema>;

export const isValidProfileDateOfBirth = (
  dateOfBirth: string,
  savedDateOfBirth?: string | null
): boolean =>
  (Boolean(dateOfBirth) && dateOfBirth === savedDateOfBirth) ||
  dateOfBirthSchema.safeParse(dateOfBirth).success;
